import { generateClient } from 'aws-amplify/data'
import { uploadData, getUrl, remove } from 'aws-amplify/storage'
import { type Schema } from '../../amplify/data/resource'
import { fetchAuthSession } from 'aws-amplify/auth'

const client = generateClient<Schema>()

export interface ContentItem {
  id: string
  userId: string
  title: string
  description?: string
  fileName: string
  fileSize?: number
  mimeType?: string
  duration?: number
  s3Key: string
  thumbnailS3Key?: string
  lightingCuesS3Key?: string
  status?: 'uploading' | 'processing' | 'ready' | 'error'
  tags?: string[]
  isGlobal?: boolean
  createdAt?: string
  updatedAt?: string
}

export interface ContentClientAccess {
  id: string
  contentId: string
  clientId: string
  assignedBy: string
  assignedAt?: string
  createdAt?: string
  updatedAt?: string
}

export interface ContentUploadRequest {
  title: string
  description?: string
  file: File
  tags?: string[]
  onProgress?: (progress: { loaded: number; total: number }) => void
}

export interface ContentStats {
  totalContent: number
  totalSize: number
  readyContent: number
  processingContent: number
}

class ContentService {
  /**
   * Upload content to S3 and create metadata record
   */
  async uploadContent(request: ContentUploadRequest): Promise<ContentItem> {
    try {
      const session = await fetchAuthSession()
      const userId = session.userSub

      if (!userId) {
        throw new Error('User not authenticated')
      }

      // Generate unique S3 key - all content in a single pool
      const timestamp = Date.now()
      const sanitizedFileName = request.file.name.replace(/[^a-zA-Z0-9.-]/g, '_')
      const uploadKey = `uploads/${timestamp}_${sanitizedFileName}`

      // Create initial metadata record with the upload key
      // The thumbnail processor will update this to the processed key after MediaConvert completes
      const { data: contentItem, errors: createErrors } = await client.models.Content.create({
        userId,
        title: request.title,
        description: request.description,
        fileName: request.file.name,
        fileSize: request.file.size,
        mimeType: request.file.type,
        s3Key: uploadKey, // Use upload key initially
        status: 'uploading',
        tags: request.tags,
        isGlobal: true, // All uploaded content starts as global
      })

      if (createErrors || !contentItem) {
        console.error('Error creating content metadata:', createErrors)
        throw new Error('Failed to create content metadata')
      }

      try {
        // Upload file to uploads prefix in main storage bucket
        // S3 event notification will automatically trigger thumbnailProcessor Lambda
        await uploadData({
          key: uploadKey,
          data: request.file,
          options: {
            contentType: request.file.type,
            onProgress: (progress) => {
              console.log('Upload progress:', progress)
              if (request.onProgress) {
                request.onProgress({
                  loaded: progress.transferredBytes || 0,
                  total: progress.totalBytes || request.file.size
                })
              }
            },
          },
        }).result

        // Update status to processing - MediaConvert will handle the rest
        const { data: updatedContent, errors: updateErrors } = await client.models.Content.update({
          id: contentItem.id,
          status: 'processing',
        })

        if (updateErrors || !updatedContent) {
          console.error('Error updating content status:', updateErrors)
          throw new Error('Failed to update content status')
        }

        return {
          id: updatedContent.id,
          userId: updatedContent.userId,
          title: updatedContent.title,
          description: updatedContent.description || undefined,
          fileName: updatedContent.fileName,
          fileSize: updatedContent.fileSize || undefined,
          mimeType: updatedContent.mimeType || undefined,
          duration: updatedContent.duration || undefined,
          s3Key: updatedContent.s3Key,
          thumbnailS3Key: updatedContent.thumbnailS3Key || undefined,
          lightingCuesS3Key: updatedContent.lightingCuesS3Key || undefined,
          status: updatedContent.status || 'ready',
          tags: updatedContent.tags?.filter((tag): tag is string => tag !== null) || undefined,
          isGlobal: updatedContent.isGlobal ?? true,
          createdAt: updatedContent.createdAt || undefined,
          updatedAt: updatedContent.updatedAt || undefined,
        }
      } catch (uploadError) {
        // Update status to error if upload fails
        await client.models.Content.update({
          id: contentItem.id,
          status: 'error',
        })
        throw uploadError
      }
    } catch (error) {
      console.error('Error in uploadContent:', error)
      throw error
    }
  }

  /**
   * Get all content items for the current user
   */
  async getContent(): Promise<ContentItem[]> {
    try {
      const session = await fetchAuthSession()
      const userId = session.userSub

      if (!userId) {
        throw new Error('User not authenticated')
      }

      const { data: content, errors } = await client.models.Content.listContentByUserIdAndCreatedAt({
        userId,
      })

      if (errors) {
        console.error('Error fetching content:', errors)
        throw new Error('Failed to fetch content')
      }

      return content.map(item => ({
        id: item.id,
        userId: item.userId,
        title: item.title,
        description: item.description || undefined,
        fileName: item.fileName,
        fileSize: item.fileSize || undefined,
        mimeType: item.mimeType || undefined,
        duration: item.duration || undefined,
        s3Key: item.s3Key,
        thumbnailS3Key: item.thumbnailS3Key || undefined,
        lightingCuesS3Key: item.lightingCuesS3Key || undefined,
        status: item.status || 'ready',
        tags: item.tags?.filter((tag): tag is string => tag !== null) || undefined,
        isGlobal: item.isGlobal ?? true,
        createdAt: item.createdAt || undefined,
        updatedAt: item.updatedAt || undefined,
      }))
    } catch (error) {
      console.error('Error in getContent:', error)
      throw error
    }
  }

  /**
   * Get content statistics
   */
  async getContentStats(): Promise<ContentStats> {
    try {
      const content = await this.getContent()
      
      const stats = content.reduce(
        (acc, item) => {
          acc.totalContent++
          acc.totalSize += item.fileSize || 0
          
          switch (item.status) {
            case 'ready':
              acc.readyContent++
              break
            case 'processing':
            case 'uploading':
              acc.processingContent++
              break
          }
          
          return acc
        },
        { totalContent: 0, totalSize: 0, readyContent: 0, processingContent: 0 }
      )

      return stats
    } catch (error) {
      console.error('Error in getContentStats:', error)
      throw error
    }
  }

  /**
   * Get a specific content item
   */
  async getContentItem(contentId: string): Promise<ContentItem | null> {
    try {
      const { data: contentItem, errors } = await client.models.Content.get({
        id: contentId,
      })

      if (errors) {
        console.error('Error fetching content item:', errors)
        throw new Error('Failed to fetch content item')
      }

      if (!contentItem) {
        return null
      }

      return {
        id: contentItem.id,
        userId: contentItem.userId,
        title: contentItem.title,
        description: contentItem.description || undefined,
        fileName: contentItem.fileName,
        fileSize: contentItem.fileSize || undefined,
        mimeType: contentItem.mimeType || undefined,
        duration: contentItem.duration || undefined,
        s3Key: contentItem.s3Key,
        thumbnailS3Key: contentItem.thumbnailS3Key || undefined,
        lightingCuesS3Key: contentItem.lightingCuesS3Key || undefined,
        status: contentItem.status || 'ready',
        tags: contentItem.tags?.filter((tag): tag is string => tag !== null) || undefined,
        isGlobal: contentItem.isGlobal ?? true,
        createdAt: contentItem.createdAt || undefined,
        updatedAt: contentItem.updatedAt || undefined,
      }
    } catch (error) {
      console.error('Error in getContentItem:', error)
      throw error
    }
  }

  /**
   * Update content metadata
   */
  async updateContent(contentId: string, updates: Partial<ContentItem>): Promise<ContentItem> {
    try {
      const { data: updatedContent, errors } = await client.models.Content.update({
        id: contentId,
        ...updates,
      })

      if (errors || !updatedContent) {
        console.error('Error updating content:', errors)
        throw new Error('Failed to update content')
      }

      return {
        id: updatedContent.id,
        userId: updatedContent.userId,
        title: updatedContent.title,
        description: updatedContent.description || undefined,
        fileName: updatedContent.fileName,
        fileSize: updatedContent.fileSize || undefined,
        mimeType: updatedContent.mimeType || undefined,
        duration: updatedContent.duration || undefined,
        s3Key: updatedContent.s3Key,
        thumbnailS3Key: updatedContent.thumbnailS3Key || undefined,
        lightingCuesS3Key: updatedContent.lightingCuesS3Key || undefined,
        status: updatedContent.status || 'ready',
        tags: updatedContent.tags?.filter((tag): tag is string => tag !== null) || undefined,
        isGlobal: updatedContent.isGlobal ?? true,
        createdAt: updatedContent.createdAt || undefined,
        updatedAt: updatedContent.updatedAt || undefined,
      }
    } catch (error) {
      console.error('Error in updateContent:', error)
      throw error
    }
  }

  /**
   * Delete content item and associated S3 objects
   */
  async deleteContent(contentId: string): Promise<void> {
    try {
      const contentItem = await this.getContentItem(contentId)
      if (!contentItem) {
        throw new Error('Content item not found')
      }

      // Delete S3 objects
      try {
        await remove({ key: contentItem.s3Key })
        if (contentItem.thumbnailS3Key) {
          await remove({ key: contentItem.thumbnailS3Key })
        }
      } catch (s3Error) {
        console.warn('Error deleting S3 objects:', s3Error)
        // Continue with metadata deletion even if S3 deletion fails
      }

      // Delete metadata record
      const { errors } = await client.models.Content.delete({
        id: contentId,
      })

      if (errors) {
        console.error('Error deleting content metadata:', errors)
        throw new Error('Failed to delete content metadata')
      }
    } catch (error) {
      console.error('Error in deleteContent:', error)
      throw error
    }
  }

  /**
   * Get signed URL for content download
   */
  async getContentUrl(s3Key: string): Promise<string> {
    try {
      const url = await getUrl({ key: s3Key })
      return url.url.toString()
    } catch (error) {
      console.error('Error getting content URL:', error)
      throw error
    }
  }

  /**
   * Get signed URL for thumbnail
   */
  async getThumbnailUrl(s3Key: string): Promise<string> {
    try {
      const url = await getUrl({ key: s3Key })
      return url.url.toString()
    } catch (error) {
      console.error('Error getting thumbnail URL:', error)
      throw error
    }
  }

  /**
   * Assign content to a client (many-to-many relationship)
   */
  async assignContentToClient(contentId: string, clientId: string): Promise<ContentClientAccess> {
    try {
      const session = await fetchAuthSession()
      const userId = session.userSub

      if (!userId) {
        throw new Error('User not authenticated')
      }

      const { data: access, errors } = await client.models.ContentClientAccess.create({
        contentId,
        clientId,
        assignedBy: userId,
        assignedAt: new Date().toISOString(),
      })

      if (errors || !access) {
        console.error('Error assigning content to client:', errors)
        throw new Error('Failed to assign content to client')
      }

      return {
        id: access.id,
        contentId: access.contentId,
        clientId: access.clientId,
        assignedBy: access.assignedBy,
        assignedAt: access.assignedAt || undefined,
        createdAt: access.createdAt || undefined,
        updatedAt: access.updatedAt || undefined,
      }
    } catch (error) {
      console.error('Error in assignContentToClient:', error)
      throw error
    }
  }

  /**
   * Remove content access from a client
   */
  async removeContentFromClient(accessId: string): Promise<void> {
    try {
      const { errors } = await client.models.ContentClientAccess.delete({
        id: accessId,
      })

      if (errors) {
        console.error('Error removing content from client:', errors)
        throw new Error('Failed to remove content from client')
      }
    } catch (error) {
      console.error('Error in removeContentFromClient:', error)
      throw error
    }
  }

  /**
   * Get all clients that have access to a content item
   */
  async getContentClients(contentId: string): Promise<ContentClientAccess[]> {
    try {
      const { data: accesses, errors } = await client.models.ContentClientAccess.listContentClientAccessByContentIdAndAssignedAt({
        contentId,
      })

      if (errors) {
        console.error('Error fetching content clients:', errors)
        throw new Error('Failed to fetch content clients')
      }

      return accesses.map(access => ({
        id: access.id,
        contentId: access.contentId,
        clientId: access.clientId,
        assignedBy: access.assignedBy,
        assignedAt: access.assignedAt || undefined,
        createdAt: access.createdAt || undefined,
        updatedAt: access.updatedAt || undefined,
      }))
    } catch (error) {
      console.error('Error in getContentClients:', error)
      throw error
    }
  }

  /**
   * Get all content accessible by a client
   */
  async getClientContent(clientId: string): Promise<ContentItem[]> {
    try {
      const { data: accesses, errors } = await client.models.ContentClientAccess.listContentClientAccessByClientIdAndAssignedAt({
        clientId,
      })

      if (errors) {
        console.error('Error fetching client content accesses:', errors)
        throw new Error('Failed to fetch client content')
      }

      // Fetch all content items for these accesses
      const contentPromises = accesses.map(access => this.getContentItem(access.contentId))
      const contentItems = await Promise.all(contentPromises)
      
      return contentItems.filter((item): item is ContentItem => item !== null)
    } catch (error) {
      console.error('Error in getClientContent:', error)
      throw error
    }
  }
}

export const contentService = new ContentService()
export default contentService